import * as React from "react"

import limmmaLogo from "../../../../assets/icon.png"
import { extentChoiceLabel } from "./public_share_settings"
import { Layer } from "../state"

export interface PublicExtentMenuEntry {
  id: number
  name: string
  level?: number | null
}

interface PublicToolbarProps {
  projectName: string
  isProcessing: boolean
  isLoading: boolean
  loadingQueue: number[]
  projectLayers: Record<number, Layer>
  manualProcessing: () => void
  autoRun: boolean
  extraInfo: string | null
  extents?: PublicExtentMenuEntry[]
  selectedExtentId?: number | null
  includeProjectOption?: boolean
  onExtentChange?: (id: number | null) => void
  /** Shown when visitors cannot switch areas. */
  activeExtentLabel?: string | null
}

export function PublicToolbar({
  projectName,
  isProcessing,
  isLoading,
  loadingQueue,
  projectLayers,
  manualProcessing,
  autoRun,
  extraInfo,
  extents = [],
  selectedExtentId = null,
  includeProjectOption = false,
  onExtentChange,
  activeExtentLabel = null,
}: PublicToolbarProps) {
  const batchSeenRef = React.useRef<Set<number>>(new Set())
  if (!isLoading && loadingQueue.length === 0) {
    if (batchSeenRef.current.size !== 0) {
      batchSeenRef.current = new Set()
    }
  } else {
    for (const id of loadingQueue) {
      batchSeenRef.current.add(id)
    }
  }
  const batchTotal = batchSeenRef.current.size
  const batchCompleted = Math.max(0, batchTotal - loadingQueue.length)
  const batchPct = batchTotal > 0 ? Math.round((batchCompleted / batchTotal) * 100) : 0

  const datasetNameById = React.useMemo(() => {
    const map = new Map<number, string>()
    for (const layer of Object.values(projectLayers)) {
      if (layer && layer.type === "DatasetLayer") {
        map.set(layer.id, layer.name || `Dataset #${layer.id}`)
      }
    }
    return map
  }, [projectLayers])

  const queuedNames = loadingQueue.map(id => datasetNameById.get(id) ?? `Dataset #${id}`)
  const currentName = queuedNames[0] ?? null
  const queueLabel = queuedNames.length === 0
    ? "Loading datasets…"
    : queuedNames.length === 1
      ? `Loading ${currentName}…`
      : `Loading ${currentName} (+${queuedNames.length - 1} more)…`

  return (
    <div className="public-toolbar d-flex align-items-center p-2 bg-white border-bottom position-relative">
      <div className="d-flex align-items-center">
        <img
          src={limmmaLogo}
          alt=""
          style={{ height: 32, width: "auto", marginRight: "0.5rem" }}
        />
        <div className="d-flex flex-column" style={{ lineHeight: 1.05 }}>
          <span style={{ fontWeight: 700, fontSize: "0.95rem", letterSpacing: "0.04em" }}>LIMMMA</span>
          <span className="text-muted" style={{ fontSize: "0.68rem" }}>University of Sussex</span>
        </div>
      </div>
      <h1
        className="h5 mb-0 text-truncate position-absolute"
        style={{ left: "50%", transform: "translateX(-50%)", maxWidth: "36%", pointerEvents: "none" }}
      >
        {projectName}
      </h1>
      <div className="d-flex align-items-center ml-auto">
        {extents.length + (includeProjectOption ? 1 : 0) >= 2 && onExtentChange ? (
          <label className="public-extent-switch mb-0 mr-2">
            <span className="public-extent-switch__label">Area</span>
            <select
              className="form-control form-control-sm"
              aria-label="Area"
              title="Switch the area the map and model use"
              disabled={isProcessing}
              value={selectedExtentId == null ? "project" : String(selectedExtentId)}
              onChange={event => {
                const raw = event.target.value
                onExtentChange(raw === "project" ? null : Number(raw))
              }}
            >
              {includeProjectOption && <option value="project">Project area</option>}
              {extents.map(extent => (
                <option key={extent.id} value={extent.id}>
                  {extentChoiceLabel(extent.name, extent.level)}
                </option>
              ))}
            </select>
          </label>
        ) : activeExtentLabel ? (
          <div className="public-extent-switch mb-0 mr-2">
            <span className="public-extent-switch__label">Area</span>
            <span className="public-extent-switch__value">{activeExtentLabel}</span>
          </div>
        ) : null}
        {!autoRun && (
        <button
          disabled={isProcessing}
          className="btn btn-success public-toolbar-execute"
          onClick={manualProcessing}
          title={isProcessing && extraInfo ? extraInfo : undefined}
        >
          {isProcessing ? (
            <>
              <i className="fas fa-circle-notch fa-spin" /> {extraInfo || "Processing..."}
            </>
          ) : (
            <>
              <i className="fas fa-play" /> Execute
            </>
          )}
        </button>
        )}
        {autoRun && isProcessing && (
          <div
            className="public-toolbar-status mr-2"
            role="status"
            aria-live="polite"
            title={extraInfo || "The model is still running"}
          >
            <i className="fas fa-circle-notch fa-spin" aria-hidden />
            <span>{extraInfo || "Model running…"}</span>
          </div>
        )}
      {(isLoading || loadingQueue.length > 0) && batchTotal > 0 && (
        <div
          className="d-flex align-items-center mr-2"
          style={{ minWidth: 220, maxWidth: 320 }}
          title={queuedNames.length > 0 ? `Pending:\n${queuedNames.join("\n")}` : undefined}
        >
          <div className="flex-grow-1">
            <div
              className="d-flex justify-content-between align-items-baseline"
              style={{ fontSize: "0.78rem", lineHeight: 1.15 }}
            >
              <span className="text-truncate" style={{ maxWidth: 220 }}>
                <i className="fas fa-cloud-download-alt mr-1 text-primary" />
                {queueLabel}
              </span>
              <span className="ml-2 text-muted" style={{ whiteSpace: "nowrap" }}>
                {batchCompleted}/{batchTotal}
              </span>
            </div>
            <div className="progress mt-1" style={{ height: "5px" }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                role="progressbar"
                style={{ width: `${batchPct}%`, transition: "width 200ms linear" }}
                aria-valuenow={batchPct}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}
